import { useCallback, useEffect, useRef, useState } from "react";
import { IFavoriteOriginWithMonths } from "../../../../types/IFavoriteOrigins";
import { DeleteTarget, PendingUndoDelete } from "../../../../types/IOriginManager";
import { getActiveOrigins, getRowKey } from "../FavoriteOriginHelper";
import {
  createFavorite,
  deleteFavorite,
  reorderFavorites,
} from "../originManagerCrudActions";

interface UseOriginManagerDeleteParams {
  selectedProduct: string;
  savedOrigins: IFavoriteOriginWithMonths[];
  newlyAddedOrigins: Record<string, IFavoriteOriginWithMonths[]>;
  setNewlyAddedOriginsByProduct: React.Dispatch<
    React.SetStateAction<Record<string, IFavoriteOriginWithMonths[]>>
  >;
  deletedRowKeysByProduct: Record<string, Set<string>>;
  setDeletedRowKeysByProduct: React.Dispatch<
    React.SetStateAction<Record<string, Set<string>>>
  >;
  setEditedSplitMonthsByProduct: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string[]>>>
  >;
  getMarketerIdentifier: () => string;
  refreshFavorites: (marketer: string) => Promise<unknown[]>;
  onSaveComplete?: () => void;
  editingRowKey: string | null;
  onDeleteActiveEditingRow: () => void;
}

export interface UseOriginManagerDeleteResult {
  pendingUndoDelete: PendingUndoDelete | null;
  handleRequestDeleteOrigin: (origin: IFavoriteOriginWithMonths) => void;
  handleUndoDelete: () => Promise<void>;
}

export const useOriginManagerDelete = ({
  selectedProduct,
  savedOrigins,
  newlyAddedOrigins,
  setNewlyAddedOriginsByProduct,
  deletedRowKeysByProduct,
  setDeletedRowKeysByProduct,
  setEditedSplitMonthsByProduct,
  getMarketerIdentifier,
  refreshFavorites,
  onSaveComplete,
  editingRowKey,
  onDeleteActiveEditingRow,
}: UseOriginManagerDeleteParams): UseOriginManagerDeleteResult => {
  const [pendingUndoDelete, setPendingUndoDelete] =
    useState<PendingUndoDelete | null>(null);
  const deleteCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const applyDeleteLocally = useCallback(
    (target: DeleteTarget) => {
      setDeletedRowKeysByProduct((prev) => {
        const next: Record<string, Set<string>> = { ...prev };
        next[selectedProduct] = new Set([
          ...(next[selectedProduct] ?? new Set<string>()),
          target.rowKey,
        ]);
        return next;
      });
      setNewlyAddedOriginsByProduct((prev) => {
        const next = { ...prev };
        next[selectedProduct] = (next[selectedProduct] ?? []).filter(
          (row) => getRowKey(row) !== target.rowKey,
        );
        return next;
      });
      setEditedSplitMonthsByProduct((prev) => {
        const next = { ...prev };
        const byRow = { ...(next[selectedProduct] ?? {}) };
        delete byRow[target.rowKey];
        next[selectedProduct] = byRow;
        return next;
      });
    },
    [
      selectedProduct,
      setDeletedRowKeysByProduct,
      setEditedSplitMonthsByProduct,
      setNewlyAddedOriginsByProduct,
    ],
  );

  const rollbackDeleteLocally = useCallback(
    (target: DeleteTarget) => {
      setDeletedRowKeysByProduct((prev) => {
        const next: Record<string, Set<string>> = { ...prev };
        const setForProduct = new Set(
          next[selectedProduct] ?? new Set<string>(),
        );
        setForProduct.delete(target.rowKey);
        next[selectedProduct] = setForProduct;
        return next;
      });
    },
    [selectedProduct, setDeletedRowKeysByProduct],
  );

  const commitDeleteOrigin = useCallback(
    async (target: DeleteTarget) => {
      const marketer = getMarketerIdentifier();
      if (!marketer) {
        throw new Error(
          "Cannot delete origin: marketer identity is unavailable.",
        );
      }

      const deletedRowKeys = new Set(
        deletedRowKeysByProduct[selectedProduct] ?? new Set<string>(),
      );
      deletedRowKeys.add(target.rowKey);
      const favorites = getActiveOrigins(
        savedOrigins,
        newlyAddedOrigins[selectedProduct] ?? [],
        deletedRowKeys,
      );

      const fullOrigin = [
        ...savedOrigins,
        ...(newlyAddedOrigins[selectedProduct] ?? []),
      ].find(
        (origin) =>
          origin.location.id === target.locationId &&
          origin.competitor === target.competitor,
      );

      if (fullOrigin) {
        await deleteFavorite(marketer, selectedProduct, fullOrigin);
      }

      await reorderFavorites(marketer, selectedProduct, favorites, getRowKey);

      await refreshFavorites(marketer);

      onSaveComplete?.();
    },
    [
      deletedRowKeysByProduct,
      getMarketerIdentifier,
      newlyAddedOrigins,
      onSaveComplete,
      refreshFavorites,
      savedOrigins,
      selectedProduct,
    ],
  );

  const restoreDeletedOrigin = useCallback(
    async (payload: PendingUndoDelete) => {
      const marketer = getMarketerIdentifier();
      if (!marketer) {
        throw new Error(
          "Cannot restore origin: marketer identity is unavailable.",
        );
      }

      const restored = payload.favoritesSnapshot.find(
        (origin) =>
          origin.location.id === payload.locationId &&
          origin.competitor === payload.competitor,
      );

      if (restored) {
        const restoredOrder = payload.favoritesSnapshot.findIndex(
          (origin) => getRowKey(origin) === payload.rowKey,
        );

        const created = await createFavorite(
          marketer,
          selectedProduct,
          restored,
          restoredOrder < 0 ? 0 : restoredOrder,
        );

        const createdIdsByRowKey = new Map<string, string>();
        if (created[0]?.favLocId) {
          createdIdsByRowKey.set(payload.rowKey, created[0].favLocId);
        }

        await reorderFavorites(
          marketer,
          selectedProduct,
          payload.favoritesSnapshot,
          getRowKey,
          createdIdsByRowKey,
        );
      }

      await refreshFavorites(marketer);

      onSaveComplete?.();
    },
    [getMarketerIdentifier, onSaveComplete, refreshFavorites, selectedProduct],
  );

  const handleRequestDeleteOrigin = useCallback(
    (origin: IFavoriteOriginWithMonths) => {
      const target: DeleteTarget = {
        rowKey: getRowKey(origin),
        locationId: origin.location.id,
        competitor: origin.competitor,
        locationName: origin.location.name,
      };
      const favoriteOrigins = getActiveOrigins(
        savedOrigins,
        newlyAddedOrigins[selectedProduct] ?? [],
        deletedRowKeysByProduct[selectedProduct] ?? new Set<string>(),
      );

      applyDeleteLocally(target);

      if (editingRowKey === target.rowKey) {
        onDeleteActiveEditingRow();
      }

      if (deleteCommitTimerRef.current) {
        clearTimeout(deleteCommitTimerRef.current);
        deleteCommitTimerRef.current = null;
      }

      setPendingUndoDelete({
        ...target,
        favoritesSnapshot: favoriteOrigins,
      });

      // Persist delete immediately so refresh reflects deletion right away.
      commitDeleteOrigin(target).catch(() => {
        rollbackDeleteLocally(target);
        setPendingUndoDelete((prev) =>
          prev?.rowKey === target.rowKey ? null : prev,
        );
        if (deleteCommitTimerRef.current) {
          clearTimeout(deleteCommitTimerRef.current);
          deleteCommitTimerRef.current = null;
        }
      });

      // Keep undo CTA visible for 5 seconds after immediate commit.
      deleteCommitTimerRef.current = setTimeout(() => {
        const currentTarget = target;
        setPendingUndoDelete((prev) =>
          prev?.rowKey === currentTarget.rowKey ? null : prev,
        );
        deleteCommitTimerRef.current = null;
      }, 5000);
    },
    [
      applyDeleteLocally,
      commitDeleteOrigin,
      deletedRowKeysByProduct,
      editingRowKey,
      newlyAddedOrigins,
      onDeleteActiveEditingRow,
      rollbackDeleteLocally,
      savedOrigins,
      selectedProduct,
    ],
  );

  const handleUndoDelete = useCallback(async () => {
    if (!pendingUndoDelete) {
      return;
    }

    const pendingPayload = pendingUndoDelete;

    if (deleteCommitTimerRef.current) {
      clearTimeout(deleteCommitTimerRef.current);
      deleteCommitTimerRef.current = null;
    }

    rollbackDeleteLocally(pendingPayload);
    setPendingUndoDelete(null);

    try {
      await restoreDeletedOrigin(pendingPayload);
    } catch (error) {
      console.error("Failed to undo origin delete.", error);
    }
  }, [pendingUndoDelete, restoreDeletedOrigin, rollbackDeleteLocally]);

  useEffect(
    () => () => {
      if (deleteCommitTimerRef.current) {
        clearTimeout(deleteCommitTimerRef.current);
      }
    },
    [],
  );

  return {
    pendingUndoDelete,
    handleRequestDeleteOrigin,
    handleUndoDelete,
  };
};
